import type { RGBColor } from '@/types'

/**
 * Convert hex color string to RGB object
 */
export function hexToRgb(hex: string): RGBColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 232, g: 213, b: 183 } // Default warm cream color
}

// ==============================================
// Oklab Color Space - Perceptually Uniform Color Interpolation
// ==============================================
// Oklab is a perceptually uniform color space designed by Björn Ottosson
// It provides much smoother color transitions than RGB because it matches
// how humans perceive color differences

interface OklabColor {
  L: number  // Lightness (0-1)
  a: number  // Green-red axis (-0.4 to 0.4)
  b: number  // Blue-yellow axis (-0.4 to 0.4)
}

/**
 * Convert linear sRGB to Oklab
 * Uses the matrix transformation from the Oklab specification
 */
function linearRgbToOklab(r: number, g: number, b: number): OklabColor {
  // Convert to linear space (gamma expansion)
  const lr = r <= 0.04045 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
  const lg = g <= 0.04045 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
  const lb = b <= 0.04045 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

  // RGB to LMS
  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb

  // LMS to Oklab
  const l_ = Math.cbrt(l)
  const m_ = Math.cbrt(m)
  const s_ = Math.cbrt(s)

  return {
    L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  }
}

/**
 * Convert Oklab to linear sRGB
 */
function oklabToLinearRgb(L: number, a: number, b: number): { r: number; g: number; b: number } {
  // Oklab to LMS
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b

  const l = l_ * l_ * l_
  const m = m_ * m_ * m_
  const s = s_ * s_ * s_

  // LMS to RGB
  let r = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s
  let g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
  let bOut = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s

  // Convert back to sRGB (gamma compression)
  r = r <= 0.0031308 ? 12.92 * r : 1.055 * Math.pow(r, 1 / 2.4) - 0.055
  g = g <= 0.0031308 ? 12.92 * g : 1.055 * Math.pow(g, 1 / 2.4) - 0.055
  bOut = bOut <= 0.0031308 ? 12.92 * bOut : 1.055 * Math.pow(bOut, 1 / 2.4) - 0.055

  // Clamp to valid range
  return {
    r: Math.max(0, Math.min(1, r)),
    g: Math.max(0, Math.min(1, g)),
    b: Math.max(0, Math.min(1, bOut)),
  }
}

/**
 * Interpolate between two colors in Oklab space for perceptually smooth transitions
 * This produces much smoother color gradients than RGB interpolation
 *
 * @param c1 - Start color (RGB 0-255)
 * @param c2 - End color (RGB 0-255)
 * @param t - Interpolation factor (0-1)
 * @returns Interpolated color (RGB 0-255)
 */
export function lerpColorOklab(c1: RGBColor, c2: RGBColor, t: number): RGBColor {
  // Convert to 0-1 range
  const lab1 = linearRgbToOklab(c1.r / 255, c1.g / 255, c1.b / 255)
  const lab2 = linearRgbToOklab(c2.r / 255, c2.g / 255, c2.b / 255)

  // Interpolate in Oklab space
  const L = lab1.L + (lab2.L - lab1.L) * t
  const a = lab1.a + (lab2.a - lab1.a) * t
  const b = lab1.b + (lab2.b - lab1.b) * t

  // Convert back to RGB
  const rgb = oklabToLinearRgb(L, a, b)

  return {
    r: Math.round(rgb.r * 255),
    g: Math.round(rgb.g * 255),
    b: Math.round(rgb.b * 255),
  }
}

/**
 * Convert RGB object to hex string
 */
export function rgbToHex(color: RGBColor): string {
  const toHex = (n: number) => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`
}

/**
 * Create a color with alpha for p5.js
 * FIX: Validates and clamps color values to prevent invalid CSS
 */
export function colorWithAlpha(color: RGBColor, alpha: number): string {
  const r = Math.max(0, Math.min(255, Math.round(color.r ?? 0)))
  const g = Math.max(0, Math.min(255, Math.round(color.g ?? 0)))
  const b = Math.max(0, Math.min(255, Math.round(color.b ?? 0)))
  const a = Math.max(0, Math.min(1, alpha ?? 1))
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

/**
 * Lerp between two colors
 */
export function lerpColor(c1: RGBColor, c2: RGBColor, t: number): RGBColor {
  return {
    r: Math.round(c1.r + (c2.r - c1.r) * t),
    g: Math.round(c1.g + (c2.g - c1.g) * t),
    b: Math.round(c1.b + (c2.b - c1.b) * t),
  }
}
